/* SalesTasks — per-rep task inbox for the Sales Portal.
 *
 * iter98.53 — Phase 2 of the Sales Portal. Tasks live ON a prospect
 * (creation flow lives in SalesProspects.jsx). This page is the
 * AGGREGATE view: all your open tasks bucketed by due date, plus a
 * tab for the things you've completed this week.
 *
 * Buckets:
 *   - Overdue        : due_date < today, status=open      (red)
 *   - Today          : due_date == today, status=open     (amber)
 *   - Upcoming       : due_date > today, status=open      (slate)
 *   - No due date    : due_date null, status=open         (muted)
 *   - Done           : status in {done, skipped}          (collapsed)
 *
 * Data source: GET /api/sales/tasks
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    CheckCircle, Circle, Warning, Calendar, ArrowRight, X, ClockCounterClockwise, CalendarBlank,
} from "@phosphor-icons/react";


const STAGE_LABEL = {
    prospect: "Prospect", discovery: "Discovery", demo: "Lab Demo", trial: "Case Trial",
    negotiation: "Negotiation", contract: "Contract", active: "Active",
};


function fmtDueDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso + "T00:00:00");
        return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", weekday: "short" });
    } catch { return iso; }
}

function daysFromToday(iso) {
    if (!iso) return null;
    try {
        const target = new Date(iso + "T00:00:00").getTime();
        const today = new Date(new Date().toISOString().slice(0, 10) + "T00:00:00").getTime();
        return Math.round((target - today) / (1000 * 60 * 60 * 24));
    } catch { return null; }
}


export default function SalesTasks() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showDone, setShowDone] = useState(false);

    const refetch = async () => {
        try {
            const r = await api.get("/sales/tasks");
            setData(r.data);
        } catch { /* swallow */ }
    };

    useEffect(() => {
        let alive = true;
        api.get("/sales/tasks")
            .then((r) => { if (alive) setData(r.data); })
            .catch(() => null)
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    const markDone = async (taskId) => {
        try {
            await api.patch(`/sales/tasks/${taskId}`, { status: "done" });
            toast.success("Task marked done");
            await refetch();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't update task.");
        }
    };

    const markOpen = async (taskId) => {
        try {
            await api.patch(`/sales/tasks/${taskId}`, { status: "open" });
            toast.success("Task reopened");
            await refetch();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't update task.");
        }
    };

    const deleteTask = async (taskId) => {
        try {
            await api.delete(`/sales/tasks/${taskId}`);
            toast.success("Task deleted");
            await refetch();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't delete task.");
        }
    };

    if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading tasks…</div>;
    if (!data) return <div className="p-8 text-sm text-muted-foreground">No tasks data.</div>;

    const { buckets, counts } = data;
    const noOpenTasks = counts.open_total === 0;

    return (
        <div className="p-4 sm:p-6 space-y-5 max-w-[1100px] mx-auto" data-testid="sales-tasks">
            {/* Header */}
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="font-display text-2xl font-bold tracking-tight">Tasks</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        {counts.open_total} open · {counts.overdue} overdue · {counts.today} due today · {counts.done} completed
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <SummaryPill label="Overdue" count={counts.overdue} tone="rose" testid="summary-overdue" />
                    <SummaryPill label="Today"   count={counts.today}   tone="amber" testid="summary-today" />
                    <SummaryPill label="Upcoming" count={counts.upcoming} tone="slate" testid="summary-upcoming" />
                </div>
            </div>

            {noOpenTasks && (
                <div className="bg-white rounded-2xl border border-emerald-200 p-8 text-center" data-testid="tasks-empty-state">
                    <CheckCircle size={32} weight="duotone" className="text-emerald-500 mx-auto mb-2" />
                    <div className="font-display text-base font-bold">All caught up.</div>
                    <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
                        No open tasks. Add one from a prospect's detail panel — it'll show up here automatically.
                    </p>
                </div>
            )}

            {/* Overdue */}
            {buckets.overdue.length > 0 && (
                <BucketSection
                    title="Overdue"
                    icon={Warning}
                    tone="rose"
                    items={buckets.overdue}
                    onMarkDone={markDone}
                    onDelete={deleteTask}
                    onNavigate={(pid) => navigate(`/sales/prospects/${pid}`)}
                    testid="bucket-overdue"
                />
            )}

            {/* Today */}
            {buckets.today.length > 0 && (
                <BucketSection
                    title="Due today"
                    icon={Calendar}
                    tone="amber"
                    items={buckets.today}
                    onMarkDone={markDone}
                    onDelete={deleteTask}
                    onNavigate={(pid) => navigate(`/sales/prospects/${pid}`)}
                    testid="bucket-today"
                />
            )}

            {/* Upcoming */}
            {buckets.upcoming.length > 0 && (
                <BucketSection
                    title="Upcoming"
                    icon={ClockCounterClockwise}
                    tone="slate"
                    items={buckets.upcoming}
                    onMarkDone={markDone}
                    onDelete={deleteTask}
                    onNavigate={(pid) => navigate(`/sales/prospects/${pid}`)}
                    testid="bucket-upcoming"
                />
            )}

            {/* No due date */}
            {buckets.no_due_date.length > 0 && (
                <BucketSection
                    title="No due date"
                    icon={CalendarBlank}
                    tone="muted"
                    items={buckets.no_due_date}
                    onMarkDone={markDone}
                    onDelete={deleteTask}
                    onNavigate={(pid) => navigate(`/sales/prospects/${pid}`)}
                    testid="bucket-no-due-date"
                />
            )}

            {/* Done — collapsed */}
            {buckets.done.length > 0 && (
                <div className="bg-white rounded-2xl border border-border" data-testid="bucket-done">
                    <button
                        type="button"
                        onClick={() => setShowDone(!showDone)}
                        className="w-full p-4 flex items-center justify-between gap-2 hover:bg-secondary/30"
                        data-testid="bucket-done-toggle"
                    >
                        <div className="flex items-center gap-2">
                            <CheckCircle size={16} weight="duotone" className="text-emerald-600" />
                            <span className="font-display text-base font-bold">Completed</span>
                            <span className="text-[11px] text-muted-foreground">({buckets.done.length})</span>
                        </div>
                        <ArrowRight
                            size={14}
                            className="text-muted-foreground"
                            style={{ transform: showDone ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}
                        />
                    </button>
                    {showDone && (
                        <div className="px-4 pb-4 space-y-2">
                            {buckets.done.map((t) => (
                                <TaskRow
                                    key={t.id}
                                    task={t}
                                    done
                                    onReopen={() => markOpen(t.id)}
                                    onDelete={() => deleteTask(t.id)}
                                    onClick={() => navigate(`/sales/prospects/${t.prospect_id}`)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


/* ─────────── Bucket section ─────────── */
const TONE_BORDER = {
    rose:  "border-rose-200 bg-rose-50/40",
    amber: "border-amber-200 bg-amber-50/40",
    slate: "border-slate-200 bg-slate-50/40",
    muted: "border-border bg-white",
};
const TONE_ICON = {
    rose:  "text-rose-600",
    amber: "text-amber-600",
    slate: "text-slate-600",
    muted: "text-muted-foreground",
};

function BucketSection({ title, icon: Icon, tone, items, onMarkDone, onDelete, onNavigate, testid }) {
    return (
        <section className={`rounded-2xl border-2 p-4 ${TONE_BORDER[tone]}`} data-testid={testid}>
            <div className="flex items-center gap-2 mb-3">
                <Icon size={16} weight="duotone" className={TONE_ICON[tone]} />
                <h2 className="font-display text-base font-bold">{title}</h2>
                <span className="text-[11px] text-muted-foreground">({items.length})</span>
            </div>
            <div className="space-y-2">
                {items.map((t) => (
                    <TaskRow
                        key={t.id}
                        task={t}
                        onMarkDone={() => onMarkDone(t.id)}
                        onDelete={() => onDelete(t.id)}
                        onClick={() => onNavigate(t.prospect_id)}
                        urgentTone={tone === "rose"}
                    />
                ))}
            </div>
        </section>
    );
}


/* ─────────── Task row ─────────── */
function TaskRow({ task, done, urgentTone, onMarkDone, onReopen, onDelete, onClick }) {
    const days = daysFromToday(task.due_date);
    const dueLabel = task.due_date ? (
        days === 0 ? "Today" :
        days === 1 ? "Tomorrow" :
        days === -1 ? "Yesterday" :
        days < 0 ? `${Math.abs(days)} days overdue` :
        `In ${days} days`
    ) : "No due date";

    return (
        <div
            className={`bg-white rounded-lg border p-3 flex items-start gap-3 ${urgentTone ? "border-rose-200" : "border-border"}`}
            data-testid={`task-row-${task.id}`}
        >
            <button
                type="button"
                onClick={done ? onReopen : onMarkDone}
                className={`mt-0.5 shrink-0 ${done ? "text-emerald-600" : "text-muted-foreground hover:text-emerald-600"}`}
                data-testid={`task-${task.id}-toggle`}
                aria-label={done ? "Reopen task" : "Mark task done"}
            >
                {done
                    ? <CheckCircle size={18} weight="fill" />
                    : <Circle size={18} weight="regular" />}
            </button>

            <button
                type="button"
                onClick={onClick}
                className="flex-1 text-left min-w-0"
                data-testid={`task-${task.id}-body`}
            >
                <div className={`text-sm font-semibold ${done ? "text-muted-foreground line-through" : ""}`}>{task.title}</div>
                {task.notes && (
                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{task.notes}</div>
                )}
                <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px]">
                    {task.clinic_name && (
                        <span className="text-muted-foreground">
                            <span className="font-semibold text-foreground">{task.clinic_name}</span>
                            {" · "}{task.doctor_name}
                            {task.prospect_stage && <> · <span className="uppercase tracking-wider">{STAGE_LABEL[task.prospect_stage]}</span></>}
                        </span>
                    )}
                </div>
            </button>

            <div className="text-right shrink-0">
                <div className={`text-[11px] font-bold ${urgentTone ? "text-rose-600" : "text-muted-foreground"}`} data-testid={`task-${task.id}-due`}>
                    {dueLabel}
                </div>
                <div className="text-[10px] text-muted-foreground">{fmtDueDate(task.due_date)}</div>
            </div>

            <button
                type="button"
                onClick={onDelete}
                className="text-muted-foreground hover:text-rose-600 shrink-0"
                data-testid={`task-${task.id}-delete`}
                aria-label="Delete task"
            >
                <X size={12} />
            </button>
        </div>
    );
}


/* ─────────── Summary pill (top-right) ─────────── */
const PILL_TONE = {
    rose:  "bg-rose-50 text-rose-700 ring-rose-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    slate: "bg-secondary text-foreground ring-border",
};
function SummaryPill({ label, count, tone, testid }) {
    return (
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ring-1 text-xs font-semibold ${PILL_TONE[tone]}`} data-testid={testid}>
            <span className="tabular-nums font-bold">{count}</span>
            <span className="opacity-80">{label}</span>
        </div>
    );
}
